// src/context/ShopifyProvider.js  (drop-in replace)

import React, { useState, useEffect } from 'react'
// We no longer need 'shopify-buy' or isomorphic-fetch.
// This shim exposes the same methods your code already calls.
import { checkoutCompat } from 'src/lib/shopify-cart-compat'
import { productCompat } from 'src/lib/shopify-product-compat'

export const ShopifyContext = React.createContext()

// Create a lightweight "client" that mimics `shopify-buy`'s surface
// so the rest of the app doesn't need refactors.
const client = {
  checkout: {
    create: (...args) => checkoutCompat.create(...args),
    fetch: (...args) => checkoutCompat.fetch(...args),
    addLineItems: (...args) => checkoutCompat.addLineItems(...args),
    updateLineItems: (...args) => checkoutCompat.updateLineItems(...args),
    removeLineItems: (...args) => checkoutCompat.removeLineItems(...args),
    // add/remove discount are available too if you used them elsewhere:
    addDiscount: (...args) => checkoutCompat.addDiscount(...args),
    removeDiscount: (...args) => checkoutCompat.removeDiscount(...args),
  },
  product: {
    fetch: (...args) => productCompat.fetch(...args),
    helpers: {
      variantForOptions: (...args) => productCompat.helpers.variantForOptions(...args),
    },
  },
}

const ShopifyProvider = ({ children, shopifyAdmin }) => {
  const initialStoreState = {
    client,
    adding: false,
    updatingItem: false,
    checkout: { lineItems: [] },
    products: [],
    shop: {},
    ...shopifyAdmin,
  }

  const [store, updateStore] = useState(initialStoreState)
  let isRemoved = false

  useEffect(() => {
    const initializeCheckout = async () => {
      const isBrowser = typeof window !== 'undefined'
      const existingCheckoutID = isBrowser ? localStorage.getItem('shopify_checkout_id') : null

      const setCheckoutInState = checkout => {
        if (isBrowser) {
          localStorage.setItem('shopify_checkout_id', checkout.id)
        }
        updateStore(prev => ({ ...prev, checkout }))
      }

      const createNewCheckout = () => store.client.checkout.create()
      const fetchCheckout = id => store.client.checkout.fetch(id)

      if (existingCheckoutID) {
        try {
          const checkout = await fetchCheckout(existingCheckoutID)
          // Cart API doesn't have completedAt on the cart; if you relied on it,
          // the fetch() wrapper returns _raw which contains the full cart.
          if (!isRemoved /* && !checkout.completedAt */) {
            setCheckoutInState(checkout)
            return
          }
        } catch (e) {
          if (isBrowser) localStorage.setItem('shopify_checkout_id', '')
        }
      }

      const newCheckout = await createNewCheckout()
      if (!isRemoved) setCheckoutInState(newCheckout)
    }

    initializeCheckout()
  }, [store.client.checkout])

  useEffect(() => () => { isRemoved = true }, [])

  return (
    <ShopifyContext.Provider
      value={{
        store,

        addVariantToCart: (variantId, quantity) => {
          if (!variantId || !quantity) return

          updateStore(prev => ({ ...prev, adding: true }))

          const { checkout, client } = store
          const checkoutId = checkout.id
          const lineItemsToUpdate = [{ variantId, quantity: parseInt(quantity, 10) }]

          return client.checkout.addLineItems(checkoutId, lineItemsToUpdate).then(nextCheckout => {
            updateStore(prev => ({ ...prev, checkout: nextCheckout, adding: false }))
          })
        },

        removeLineItem: (checkoutID, lineItemID) => {
          // NOTE: Cart API expects *cart line IDs* here (the shim fetch returns them on lineItems[].id)
          return client.checkout.removeLineItems(checkoutID, [lineItemID]).then(nextCheckout => {
            updateStore(prev => ({ ...prev, checkout: nextCheckout }))
          })
        },

        updateLineItem: (checkoutID, lineItemID, quantity) => {
          const lineItemsToUpdate = [{ id: lineItemID, quantity: parseInt(quantity, 10) }]

          updateStore(prev => ({ ...prev, updatingItem: lineItemID }))

          return client.checkout.updateLineItems(checkoutID, lineItemsToUpdate).then(nextCheckout => {
            updateStore(prev => ({ ...prev, checkout: nextCheckout, updatingItem: false }))
          })
        },
      }}
    >
      {children}
    </ShopifyContext.Provider>
  )
}

export default ShopifyProvider
